// Color palette catalogue — Commit 6a.
//
// Why this exists
// ---------------
// ChoroplethMap shipped with a single hardcoded amber→red sequential
// palette. Semantically right for temperature; wrong for rainfall
// (expect blue), vegetation (green), pollutants (red intensifies the
// "bad" signal), or comparison maps where diverging is needed.
//
// This catalogue declares palette intent so the renderer can pick a
// sensible default per metric and users can override per visual.
//
// Shape
// -----
//   id          — stable identifier (saved-visual configs reference this)
//   name        — human-readable label for the dropdown
//   hint        — optional short "what it's for" word (e.g. 'water',
//                 'population'); paletteOptionLabel() appends it as
//                 "name (hint)". This is the only place in the UI that
//                 tells a user what a palette is semantically FOR, so a
//                 palette bound to a metric type should carry one. Omit it
//                 for palettes without a metric affinity (viridis) or
//                 whose `name` already states its one descriptor.
//   type        — 'sequential' | 'diverging' | 'qualitative'
//   stops       — array of hex strings, low → high (sequential),
//                 negative → zero → positive (diverging),
//                 distinct colors (qualitative)
//   intentHint  — metric-type hint ('temperature' | 'water' | 'vegetation' |
//                 'pollution' | 'incidence' | 'generic'); the renderer
//                 prefers a matching palette when no explicit choice
//                 is provided
//   colorBlindSafe — boolean; true palettes survive deuteranopia /
//                    protanopia. Used to filter for accessible-only mode.
//
// Style notes
// -----------
// - 5–7 stops per sequential palette. More creates a smooth gradient;
//   fewer creates discrete tiers which is desirable for some metrics
//   (e.g. risk-level-like incidence buckets).
// - All hex strings are 6-char, no leading "#" — matches the
//   pptxgenjs style and avoids parsing inconsistency across the
//   codebase. ChoroplethMap adds "#" at render time.
// - Diverging palettes have an odd number of stops with the
//   midpoint being a light/neutral color.

// `risk-bands` below is the one entry that is NOT free to pick its own
// colours: the map fill resolves risk levels through riskLevelTierRgb(), so a
// second hardcoded copy here is how the legend ends up describing a map it is
// not painting. Derived from the same table instead.
import { SEVERITY_TIER_HEX, SEVERITY_TIER_ORDER } from '../components/maps/severityTierTheme.mjs'

const RISK_BAND_STOPS = Object.freeze(SEVERITY_TIER_ORDER.map((tier) => SEVERITY_TIER_HEX[tier]))

export const PALETTES = {
  // ---- Sequential ----
  'amber-red': {
    id: 'amber-red',
    name: 'Amber → Red',
    hint: 'heat',
    type: 'sequential',
    stops: ['fef3c7', 'fde68a', 'fbbf24', 'f97316', 'ef4444', 'dc2626', '991b1b'],
    intentHint: 'temperature',
    colorBlindSafe: false,
    description: 'Default heat palette. Best for temperature, fire risk, intensity-of-bad metrics.',
  },
  'blue-teal': {
    id: 'blue-teal',
    name: 'Blue → Teal',
    hint: 'water',
    type: 'sequential',
    stops: ['e0f2fe', 'bae6fd', '7dd3fc', '38bdf8', '0ea5e9', '0369a1', '0c4a6e'],
    intentHint: 'water',
    colorBlindSafe: true,
    description: 'Cool blues. Best for rainfall, humidity, water indices, anything where higher = wetter.',
  },
  'viridis': {
    id: 'viridis',
    name: 'Viridis (perceptually uniform)',
    // No `hint` field — unlike the others this one isn't tied to a metric
    // type (intentHint: 'generic'), and `name` already carries its one
    // descriptor. Appending a hint would double up the parenthetical.
    type: 'sequential',
    stops: ['440154', '443983', '31688e', '21918c', '35b779', '90d743', 'fde725'],
    intentHint: 'generic',
    colorBlindSafe: true,
    description: 'The Matplotlib default. Perceptually uniform, color-blind safe. Best for unknown / generic metrics.',
  },
  'green-shades': {
    id: 'green-shades',
    name: 'Green shades',
    hint: 'vegetation',
    type: 'sequential',
    stops: ['f0fdf4', 'dcfce7', 'bbf7d0', '86efac', '4ade80', '16a34a', '14532d'],
    intentHint: 'vegetation',
    colorBlindSafe: false,
    description: 'Light → dark green. Best for NDVI, EVI, vegetation indices.',
  },
  'purple-pink': {
    id: 'purple-pink',
    name: 'Purple → Pink',
    hint: 'pollution',
    type: 'sequential',
    stops: ['fdf4ff', 'f5d0fe', 'e879f9', 'c026d3', 'a21caf', '86198f', '581c87'],
    intentHint: 'pollution',
    colorBlindSafe: false,
    description: 'Purple-pink. Useful for pollutant concentrations where you want a non-temperature palette.',
  },
  'yellow-brown': {
    id: 'yellow-brown',
    name: 'Yellow → Brown',
    hint: 'population',
    type: 'sequential',
    // ColorBrewer YlOrBr, the ramp WHO/UN population atlases use — client
    // feedback named this explicitly to get population off 'blue-teal'
    // (semantically WATER here — see defaultPaletteFor below) and off
    // 'amber-red' (already TEMPERATURE; reusing it would just swap one
    // collision for another).
    stops: ['ffffe5', 'fff7bc', 'fee391', 'fec44f', 'fe9929', 'd95f0e', '8c510a'],
    intentHint: 'population',
    colorBlindSafe: true,
    description: 'WHO/UN population-atlas convention. Best for population density and demographic stocks.',
  },

  // ---- Diverging ----
  'red-blue': {
    id: 'red-blue',
    name: 'Red ↔ Blue (diverging)',
    type: 'diverging',
    stops: ['1d4ed8', '93c5fd', 'f5f5f5', 'fca5a5', 'dc2626'],
    intentHint: 'residual',
    colorBlindSafe: true,
    description: 'Diverging blue→white→red. Best for residuals, anomalies, "above vs below" signals.',
  },
  'purple-orange': {
    id: 'purple-orange',
    name: 'Purple ↔ Orange (diverging)',
    type: 'diverging',
    stops: ['7c3aed', 'c4b5fd', 'f8fafc', 'fdba74', 'ea580c'],
    intentHint: 'residual',
    colorBlindSafe: true,
    description: 'Diverging purple→neutral→orange. Color-blind safe alternative to red-blue.',
  },

  // ---- Qualitative (for categorical metrics like risk level) ----
  'risk-bands': {
    id: 'risk-bands',
    name: 'Risk bands (low/med/high/critical)',
    type: 'qualitative',
    stops: RISK_BAND_STOPS,
    intentHint: 'risk',
    colorBlindSafe: false,
    description: 'Categorical low/medium/high/critical for risk-level metrics, in the same tier colours the map fill paints. Not for continuous values.',
  },
  'tableau10': {
    id: 'tableau10',
    name: 'Tableau 10',
    type: 'qualitative',
    stops: ['4e79a7', 'f28e2c', 'e15759', '76b7b2', '59a14f',
            'edc949', 'af7aa1', 'ff9da7', '9c755f', 'bab0ac'],
    intentHint: 'generic',
    colorBlindSafe: false,
    description: 'Tableau\'s palette for categorical / qualitative breakdowns.',
  },
}

// Deck-inspired 16-step green->yellow->orange->red ramp, from the IEWRS
// conference deck (slide 48, malaria seasonal cases). Sequential INTENSITY for
// banded continuous data — not a risk-tier ramp, so it is not what
// DESIGN_SYSTEM §3 prohibition 1 bans; risk words resolve through
// severityTierTheme.mjs and never through this.
//
// It lives here because MapLegend.jsx and MapLibreChoropleth.jsx each kept
// their own copy while both claiming to be in sync "byte-for-byte". Measured
// 2026-09-02: all 16 of 16 positions differed — they were never the same
// array. MapLibreChoropleth's copy was also built by spreading green-shades +
// amber-red + risk-bands[3] + heat.at(-1), and those last two are ALREADY the
// last two amber-red stops, so its top two bands were exact duplicates of the
// two below them: a "16-band" scale that flattened and alternated at the top
// instead of intensifying. One exported array is the only way that claim can
// stay true.
export const DECK_GREEN_RED_16 = [
  '#006d2c', '#238b45', '#41ab5d', '#74c476', '#a1d99b', '#c7e9c0',
  '#ffffcc', '#ffeda0', '#fed976', '#feb24c',
  '#fd8d3c', '#fc4e2a', '#e31a1c',
  '#bd0026', '#800026', '#67001f',
]

// ---- Helpers ----

/**
 * Pick a sensible palette id for a metric name. Heuristic — best-effort
 * mapping based on metric_type substring. Returns 'amber-red' (current
 * default) when no hint matches.
 */
export function defaultPaletteFor(metricType) {
  if (!metricType) return 'amber-red'
  const m = String(metricType).toLowerCase()
  if (m.includes('rainfall') || m.includes('humidity') || m.includes('water') || m.includes('ndwi')) {
    return 'blue-teal'
  }
  // Population / demographic stocks — checked before the water branch would
  // ever matter (no overlap in substrings) but placed here so it reads next
  // to its sibling checks. This is the guardrail path used when the registry
  // API is unreachable, so it must agree with what the registry itself seeds
  // (see 0056_population_display_fix.sql).
  if (m.includes('population') || m.includes('pop_density') || m.includes('density') || m.includes('demographic')) {
    return 'yellow-brown'
  }
  if (m.includes('ndvi') || m.includes('evi') || m.includes('vegetation') || m.includes('chlorophyll') || m.includes('reci')) {
    return 'green-shades'
  }
  if (m.includes('pm25') || m.includes('no2') || m.includes('so2') || m.includes('o3') || m.includes('hcho') ||
      m.includes('pollut') || m.includes('carbon_monoxide')) {
    return 'purple-pink'
  }
  if (m.includes('temperature') || m.includes('tmax') || m.includes('tmin') || m.includes('lst') ||
      m.includes('thermal') || m.includes('heat')) {
    return 'amber-red'
  }
  // Risk-level columns are categorical
  if (m === 'risk_level' || m === 'risklevel') return 'risk-bands'
  return 'amber-red'
}

/**
 * Return the catalogue grouped by type so the UI can render an organised
 * dropdown.
 */
export function paletteOptions() {
  return Object.values(PALETTES)
}

/**
 * Palette dropdown option lists, filtered by type. The palette picker in
 * VisualItemCreator drives continuous map/chart scales — a hardcoded copy of
 * this list is how 'yellow-brown' shipped registered here but unreachable
 * from the UI (D4). Exporting the derivation means registering a new
 * sequential/diverging palette above is enough; no second edit to forget.
 * Qualitative palettes (risk-bands, tableau10) are deliberately excluded —
 * they're for categorical metrics, not this control.
 */
export function sequentialPaletteOptions() {
  return Object.values(PALETTES).filter(p => p.type === 'sequential')
}
export function divergingPaletteOptions() {
  return Object.values(PALETTES).filter(p => p.type === 'diverging')
}

/**
 * Dropdown label for a palette — "name (hint)" when the catalogue entry
 * carries a `hint`, bare `name` otherwise. Centralised here so a palette's
 * semantic-use label has exactly one place to define, same as the id/type
 * filtering above.
 */
export function paletteOptionLabel(palette) {
  return palette?.hint ? `${palette.name} (${palette.hint})` : palette?.name ?? ''
}

/**
 * Pick a color stop from a sequential palette by ratio (0–1).
 * Generalises the inline logic that was previously hardcoded in
 * ChoroplethMap::getSequentialColor.
 */
export function colorForRatio(palette, ratio) {
  const stops = palette.stops
  if (!stops?.length) return '#e5e7eb'
  const clamped = Math.max(0, Math.min(1, ratio))
  const idx = Math.min(Math.floor(clamped * stops.length), stops.length - 1)
  return '#' + stops[idx]
}

/**
 * Diverging color picker — symmetric around 0, ratio is value /
 * maxAbsValue. Pinned thresholds at ±0.3, ±0.6.
 */
export function colorForDivergingRatio(palette, ratio) {
  const stops = palette.stops
  if (!stops?.length) return '#e5e7eb'
  if (ratio < -0.6) return '#' + stops[0]
  if (ratio < -0.3) return '#' + stops[1]
  if (ratio < 0.3)  return '#' + stops[Math.floor(stops.length / 2)]
  if (ratio < 0.6)  return '#' + stops[stops.length - 2]
  return '#' + stops[stops.length - 1]
}

/**
 * Resample a hex-colour stop array into exactly N discrete band colours,
 * nearest-index sampling.
 *
 * Visual Creator bug #3/#4 root cause — the choropleth's banded fill
 * (MapLibreChoropleth's getFillColor, legendBanded mode) always rendered a
 * hardcoded 16-colour green→red ramp (DECK_GREEN_RED_16) no matter which
 * palette the user picked, while the legend's own banded renderer
 * (MapLegend's bandColors) only used a caller-supplied palette when its
 * stop COUNT happened to exactly equal the band count (16) — which no
 * catalogue palette above ever does (they're 5–7 stops) — so it silently
 * fell back to that same hardcoded ramp too. Both call sites now resample
 * whichever palette actually resolved (the ONE consolidated palette
 * control — see VisualItemCreator/MapLibreChoropleth) through this single
 * function, so the polygon fill and the legend gradient can never drift
 * apart again: same input stops in, same N colours out, everywhere.
 */
export function sampleBandedStops(stops, n) {
  const clean = Array.isArray(stops) ? stops.filter(Boolean) : []
  if (!clean.length || !Number.isFinite(n) || n <= 0) return []
  if (clean.length === n) return [...clean]
  return Array.from({ length: n }, (_, i) => {
    const idx = Math.min(clean.length - 1, Math.round((i * (clean.length - 1)) / Math.max(1, n - 1)))
    return clean[idx]
  })
}

/**
 * Resolve the banded fill ramp for a canonical palette — the single array
 * both the choropleth fill (MapLibreChoropleth's bandColorFromRamp) and its
 * legend (MapLegend) must paint from, so they can never disagree (Visual
 * Creator bug #3's whole reason to exist).
 *
 * D2 fix (design review, 2026-09-02): band count is ALWAYS the palette's
 * own stop count — 5-7 for every sequential/diverging palette in this
 * catalogue (see the "Style notes" above) — never a caller-supplied number.
 * Every real caller previously requested a hardcoded 16 unconditionally,
 * which upsampled a 7-stop palette by repeating each hex across ~2-3
 * adjacent bins: 9 of the 15 interior class boundaries painted identically,
 * violating DESIGN_SYSTEM.md §2.4's "5-7 bands maximum" (the retired
 * 16-band deck ramp this rule exists to prevent). Delegates to
 * sampleBandedStops(stops, stops.length), which is a no-op passthrough for
 * every catalogue palette here — this just makes that bound explicit and
 * gives it one testable name instead of leaving each call site to
 * (mis)compute its own band count.
 */
export function resolveBandedRamp(stops) {
  const clean = Array.isArray(stops) ? stops.filter(Boolean) : []
  if (!clean.length) return null
  return sampleBandedStops(clean, clean.length)
}

/**
 * The other half of the D2 fix — MapLibreChoropleth's own `banded` count
 * prop to MapLegend, extracted to a named function so it's testable at the
 * call site rather than only as a hand-reproduced formula. Every real
 * caller (VisualItemCreator.jsx, SeasonalCompareView.jsx) still passes the
 * legacy legendBanded={16}; once bandedRamp resolves, ITS length must win —
 * MapLegend's own banded branch only trusts palette.stops as given when
 * `explicit.length === banded` (MapLegend.jsx), so handing it the stale 16
 * against a 7-stop resolved ramp fails that check and silently falls
 * MapLegend back to its internal 16-step DECK_GREEN_RED_16 sampling,
 * reintroducing the exact fill/legend mismatch resolveBandedRamp fixes.
 */
export function resolveBandedLegendCount(bandedRamp, legendBanded) {
  return bandedRamp ? bandedRamp.length : legendBanded
}

/**
 * Pick which band (0..bandCount-1) a numeric value falls into.
 *
 *   mode = 'linear'   — evenly spaced between min and max (default).
 *   mode = 'quantile' — caller supplies `thresholds` (bandCount-1 breakpoints,
 *                       e.g. from computeScaleStops({mode:'quantile', …}));
 *                       the band is however many thresholds the value clears.
 *
 * Returns null for non-finite values (caller renders "no data" for those).
 */
export function bandIndexForValue(value, { min, max, bandCount, mode = 'linear', thresholds = null }) {
  if (value == null || !Number.isFinite(value)) return null
  if (!Number.isFinite(bandCount) || bandCount <= 0) return null
  if (mode === 'quantile' && Array.isArray(thresholds) && thresholds.length) {
    let idx = 0
    for (const t of thresholds) {
      if (value >= t) idx += 1
      else break
    }
    return Math.min(bandCount - 1, idx)
  }
  const range = (max - min) || 1
  const ratio = Math.max(0, Math.min(0.999999, (value - min) / range))
  return Math.min(bandCount - 1, Math.floor(ratio * bandCount))
}

/**
 * Compute scale stops based on data and mode. Returns an array of
 * numeric thresholds the renderer maps to palette stops.
 *
 * mode = 'linear'   — evenly spaced between min and max
 * mode = 'quantile' — equal-count buckets (best for skewed data; one
 *                     outlier doesn't flatten the rest of the map)
 * mode = 'fixed'    — caller supplies fixedStops array; we pass through
 */
export function computeScaleStops({ values, mode = 'linear', palette, fixedStops = null }) {
  const n = palette?.stops?.length || 7
  if (!values?.length) return []

  if (mode === 'fixed' && fixedStops?.length) {
    return [...fixedStops].sort((a, b) => a - b)
  }
  if (mode === 'quantile') {
    const sorted = [...values].sort((a, b) => a - b)
    const stops = []
    for (let i = 1; i < n; i++) {
      const idx = Math.floor((sorted.length * i) / n)
      stops.push(sorted[Math.min(idx, sorted.length - 1)])
    }
    return stops
  }
  // linear (default)
  const min = Math.min(...values)
  const max = Math.max(...values)
  const step = (max - min) / n
  const stops = []
  for (let i = 1; i < n; i++) stops.push(min + step * i)
  return stops
}
